/** The loading-related slice of an RTK Query (lazy) list-query result. */
type ListQueryLoadingState = {
    /** True only while the hook's very first request is in flight. */
    isLoading: boolean;
    /** True while any request is in flight. */
    isFetching: boolean;
    /** Data for the *current* args — undefined while fetching an arg combination
     *  that has no cache entry yet (unlike `data`, which lags behind and keeps the
     *  previous combination's rows). */
    currentData?: unknown;
};

/**
 * Splits RTK Query list-query flags into the two loading states a list page cares about:
 *
 * - `isLoading` (blocking) while fetching a filter/sort combination with no cache entry
 *   yet, i.e. the initial load and filter changes. The page should replace the stale list
 *   with a skeleton (e.g. by withholding `data` from the table, or skipping its own row
 *   rendering) for the duration.
 * - `isFetching` (background) while cached rows can stay visible in the meantime, i.e.
 *   load-more merging into the same cache entry (`accumulatedQueryWithFilters` keys the
 *   cache without `page`) and revalidation of an already-cached combination. The table
 *   appends its skeleton below the rows.
 *
 * The split hinges on `currentData`: it is undefined only while fetching args the cache
 * has nothing for yet.
 */
export const tableLoadingProps = ({ isLoading, isFetching, currentData }: ListQueryLoadingState) => ({
    isLoading: isLoading || (isFetching && currentData === undefined),
    isFetching: isFetching && currentData !== undefined,
});
